import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import _get from 'lodash/get';

import { Btn, Link } from 'src/components-v2/Inputs';
import { Spacer, spacerAdapter } from 'src/components-v2/Layout';
import { CmsMediaSwitch } from 'src/components-v2/Media/CmsMediaSwitch';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { CmsComponents, Contentful, targetMap } from 'src/lib/constants';
import { makeImageQueryString, makeImageSource } from 'src/lib/utils';
import Author from './Author';
import ButtonGroup from './ButtonGroup';
import CmsStatsBlock from './CmsStatsBlock';
import CtaBlockWithImage from './CtaBlockWithImage';
import DecorativeText from './DecorativeText';
import { EmbeddedEntryButton } from './EmbeddedEntryButton';
import Pill from './Pill';
import QuoteWithImage from './QuoteWithImage';
import { Icon } from 'src/components-v2/DataDisplay';
import RawHtml from 'src/components/helpers/RawHtml/RawHtml';

const EmbeddedEntrySwitch = (
  { video, asset },
  theme,
  contentTypeOverrides = {},
) => {
  const track = useTrack();
  return {
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const componentType = _get(
        node,
        Contentful.EMBEDDED_ENTRY_CONTENT_TYPE_ID,
      );

      const fields = node?.data?.target?.fields;

      if (contentTypeOverrides[componentType]) {
        return contentTypeOverrides[componentType]({
          sys: node?.data?.target?.sys,
          fields,
        });
      }

      switch (componentType) {
        case CmsComponents.ASSET:
          const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(fields);
          const width = asset?.sources?.width ?? fields?.width ?? asset?.width;
          return (
            <CmsMediaSwitch
              asset={fields}
              width={fields?.width || asset?.width}
              height={fields?.height || asset?.height}
              queryString={{
                xs: asset?.queryString?.xs,
                sm: asset?.queryString?.sm,
                md: asset?.queryString?.md,
                lg: asset?.queryString?.lg,
                xl: asset?.queryString?.xl,
              }}
              sys={node?.data?.target?.sys}
              sources={{
                xs: makeImageQueryString({
                  src: smImgSrc,
                  width,
                }),
                sm: makeImageQueryString({
                  src: smImgSrc,
                  width,
                }),
                md: makeImageQueryString({
                  src: mdImgSrc,
                  width,
                }),
                lg: makeImageQueryString({
                  src: lgImgSrc,
                  width,
                }),
                xl: makeImageQueryString({
                  src: lgImgSrc,
                  width,
                }),
              }}
            />
          );
        case CmsComponents.BUTTON:
          return <EmbeddedEntryButton fields={fields} node={node} />;
        case CmsComponents.BUTTON_GROUP:
          return <ButtonGroup node={node} />;
        case CmsComponents.CTA_BLOCK_WITH_IMAGE:
          return <CtaBlockWithImage node={node} />;
        case CmsComponents.QUOTE_WITH_IMAGE:
          return <QuoteWithImage node={node} />;
        case CmsComponents.SPACER:
          return <Spacer {...spacerAdapter(node?.data?.target)} />;
        case CmsComponents.STAT_BLOCK:
          return (
            <CmsStatsBlock
              fields={node?.data?.target?.fields}
              sys={node?.data?.target?.sys}
            />
          );
        case CmsComponents.HYPERLINK:
          return (
            <Btn
              variant={'tertiary'}
              href={fields?.url}
              target={targetMap[fields?.target]}
              trackingId={fields?.trackingId || ''}
              sys={node?.data?.target?.sys}
            >
              {fields?.text}
            </Btn>
          );
        case CmsComponents.ANCHOR:
          return (
            <span
              id={fields.anchorId}
              key={`anchor-${fields.anchorId}`}
              data-testid={fields.anchorId}
            ></span>
          );
        case CmsComponents.AUTHOR:
          return <Author node={node} />;
        case CmsComponents.RAW_HTML:
          return (
            <RawHtml
              as='div'
              className='raw-html'
              body={fields?.body}
              entryId={node?.data?.target?.sys?.id}
            />
          );

        default:
          return null;
      }
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      const componentType = _get(
        node,
        Contentful.EMBEDDED_ENTRY_CONTENT_TYPE_ID,
      );
      const fields = node?.data?.target?.fields;

      if (contentTypeOverrides[componentType]) {
        return contentTypeOverrides[componentType]({
          sys: node?.data?.target?.sys,
          fields,
        });
      }

      switch (componentType) {
        case CmsComponents.DECORATIVE_TEXT:
          return <DecorativeText node={node} />;
        case CmsComponents.HYPERLINK:
          const trackingId = fields?.trackingId || '';
          const onClick = {
            onClick: () => {
              track({ type: trackingId });
            },
          };
          return (
            <Link href={fields?.url}>
              <a
                target={targetMap[fields?.target]}
                {...(trackingId !== '' && onClick)}
              >
                {fields?.text}
              </a>
            </Link>
          );
        case CmsComponents.PILL:
          return <Pill node={node} />;
        case CmsComponents.ICON:
          const icon = fields?.icon;
          return (
            <span className='icon-wrapper'>
              <Icon icon={icon} />
            </span>
          );
        case CmsComponents.RAW_HTML:
          return (
            <RawHtml
              as='span'
              className='raw-html'
              body={fields?.body}
              entryId={node?.data?.target?.sys?.id}
            />
          );
        default:
          return null;
      }
    },
  };
};

export default EmbeddedEntrySwitch;
